import clientSocketManager, { SOCKET_DOMAINS } from './socketManager';

export { SOCKET_DOMAINS };

export const initiateSocket = (url, token, room = null) => {
  const connectionId = clientSocketManager.generateConnectionId(url, token, room);
  
  // Check if connection already exists
  if (clientSocketManager.isConnectionActive(connectionId)) {
    return clientSocketManager.getConnectionInfo(connectionId).socket;
  }

  const socket = clientSocketManager.createConnection(url, token, room);
  
  if (socket) {
    socket.emit("join", { access_token: token, room });
  }

  return socket;
};

/**
 * Initiate support chat socket with proper cleanup
 */
export const initiateSupportChatSocket = (url, token, room = null) => {
  const connectionId = clientSocketManager.generateConnectionId(url, token, `support_${room}`);
  
  // Check if connection already exists
  if (clientSocketManager.isConnectionActive(connectionId)) {
    return clientSocketManager.getConnectionInfo(connectionId).socket;
  }

  const socket = clientSocketManager.createConnection(url, token, room, {
    // Support chat specific options
    reconnectionAttempts: 3,
  });
  
  if (socket) {
    socket.emit("join_support_chat", { access_token: token, room });
  }

  return socket;
};

/**
 * Initiate notification socket with proper cleanup
 */
export const initiateNotificationSocket = (url, token, room = null) => {
  const connectionId = clientSocketManager.generateConnectionId(url, token, `notification_${room}`);
  
  // Check if connection already exists
  if (clientSocketManager.isConnectionActive(connectionId)) {
    return clientSocketManager.getConnectionInfo(connectionId).socket;
  }

  const socket = clientSocketManager.createConnection(url, token, room);
  
  if (socket) {
    socket.emit("notification_join", { access_token: token, room });
  }

  return socket;
};

/**
 * Disconnect socket with proper cleanup
 */
export const disconnectSocket = (socket) => {
  if (socket) {
    // Find and cleanup the connection
    const connections = Array.from(clientSocketManager.activeConnections.values());
    const connectionInfo = connections.find(conn => conn.socket === socket);
    
    if (connectionInfo) {
      clientSocketManager.disconnectConnection(connectionInfo.connectionId);
    } else {
      // Fallback to direct disconnect
      socket.disconnect();
    }
  }
};

/**
 * Subscribe to socket event with automatic cleanup tracking
 */
export const subscribeToEvent = (socket, event, cb) => {
  if (!socket) {
    throw new Error("Socket connection missing");
  }

  // Find the connection info
  const connections = Array.from(clientSocketManager.activeConnections.values());
  const connectionInfo = connections.find(conn => conn.socket === socket);
  
  if (connectionInfo) {
    // Use socket manager for proper cleanup tracking
    return clientSocketManager.subscribeToEvent(connectionInfo.connectionId, event, cb);
  } else {
    // Fallback to direct subscription
    socket.on(event, (msg) => {
      cb(msg);
    });
  }
};

/**
 * Send event with activity tracking
 */
export const sendEvent = (socket, event, message, token) => {
  if (!socket) {
    return false;
  }

  // Find the connection info
  const connections = Array.from(clientSocketManager.activeConnections.values());
  const connectionInfo = connections.find(conn => conn.socket === socket);
  
  if (connectionInfo) {
    // Use socket manager for activity tracking
    return clientSocketManager.sendEvent(connectionInfo.connectionId, event, message);
  } else {
    // Fallback to direct emit
    socket.emit(event, message, token);
    return true;
  }
};

/**
 * Cleanup all socket connections
 */
export const cleanupAllSockets = () => {
  clientSocketManager.cleanupAllConnections();
};

/**
 * Get socket connection statistics
 */
export const getSocketStats = () => {
  return clientSocketManager.getStats();
};

/**
 * Add cleanup callback for component unmount
 */
export const addSocketCleanupCallback = (callback) => {
  clientSocketManager.addCleanupCallback(callback);
};

/**
 * Remove cleanup callback
 */
export const removeSocketCleanupCallback = (callback) => {
  clientSocketManager.removeCleanupCallback(callback);
};
